import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/account/Navbar";
import { petAPI, clinicAPI, createClinicAppointment, authUtils } from "../../services/api";
import { useAuth } from "../../hooks/useAuth";
import { resolveImageUrl, withPlaceholder } from "../../utils/imageUtils";
import UserAverageRating from "../../components/social/UserAverageRating";
import "../../styles/guardiandash.css";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { useFlatpickrUserType } from "../../hooks/useFlatpickrUserType";

const AppointmentRequest = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const { isAuthenticated, user, getUserType } = useAuth();
  const userType = getUserType();

  // Get the correct dashboard URL based on user type
  const getDashboardUrl = () => {
    return userType === 'SERVICE_PROVIDER'
      ? '/medical/dashboard/petshopowner'
      : '/medical/dashboard/petguardian';
  };
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();

  // Apply user type color to Flatpickr calendars globally
  useFlatpickrUserType();

  // Check if a pet was pre-selected from Guardian Dashboard
  const preSelectedPet = location.state?.selectedPet;

  // Main flow states
  const [currentFlow, setCurrentFlow] = useState(preSelectedPet ? 'booking-panel' : 'pet-selection'); // 'pet-selection', 'booking-panel', 'confirmation'
  const [panelStep, setPanelStep] = useState(1); // 1: clinic, 2: vet, 3: date-time

  // Data states
  const [pets, setPets] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [clinicMembers, setClinicMembers] = useState([]);
  const [workingHours, setWorkingHours] = useState([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);

  // Selection states
  const [selectedPet, setSelectedPet] = useState(preSelectedPet || null);
  const [selectedClinicId, setSelectedClinicId] = useState(null);
  const [selectedVet, setSelectedVet] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [reasonForVisit, setReasonForVisit] = useState("");

  // UI states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [clinicSearchTerm, setClinicSearchTerm] = useState("");

  // Refs
  const dateInputRef = useRef(null);
  const datePickerRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const selectedVetRef = useRef(null);

  // Helper functions for time conversion
  const timeToMinutes = (timeString) => {
    if (!timeString || typeof timeString !== 'string') return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return 0;
    return hours * 60 + minutes;
  };

  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Redirect if not authenticated
  // Cleanup calendar on component unmount
  useEffect(() => {
    return () => {
      cleanupCalendar();
    };
  }, []);

  // Fetch pets on component mount
  useEffect(() => {
    const fetchPets = async () => {
      try {
        setLoading(true);
        const response = await petAPI.getMyPets();
        setPets(response.data.data || []);
      } catch (err) {
        setError(t("appointmentRequest.errors.loadPets"));
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchPets();
    }
  }, [isAuthenticated, t]);

  // Debounced search function
  const debouncedSearch = useCallback((searchTerm) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setLoading(true);

        // Only search if search term has at least 2 characters
        if (searchTerm.trim().length >= 2) {
          const response = await clinicAPI.searchClinics(searchTerm);
          const clinicsData = response.data?.data || response.data || [];
          // Filter to only show verified clinics
          const verifiedClinics = clinicsData.filter(clinic => {
            const status = clinic.status || clinic.verification_status;
            // Check if verified (status === 'verified' or is_verified === true)
            return status === 'verified' || clinic.is_verified === true || clinic.verified === true;
          });
          setClinics(verifiedClinics);
        } else if (searchTerm.trim().length === 0) {
          // If search is empty, load all clinics
          const response = await clinicAPI.getClinics();
          const clinicsData = response.data?.data?.clinics || response.data || [];
          // Filter to only show verified clinics
          const verifiedClinics = Array.isArray(clinicsData) ? clinicsData.filter(clinic => {
            const status = clinic.status || clinic.verification_status;
            // Check if verified (status === 'verified' or is_verified === true)
            return status === 'verified' || clinic.is_verified === true || clinic.verified === true;
          }) : [];
          setClinics(verifiedClinics);
        } else {
          // If less than 2 characters, clear results
          setClinics([]);
        }
      } catch (err) {
        console.error("Search error:", err);
        setError(t("appointmentRequest.errors.searchClinics"));
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms delay
  }, [t]);

  // Fetch clinics when panel opens or search term changes
  useEffect(() => {
    if (currentFlow === 'booking-panel' && panelStep === 1) {
      debouncedSearch(clinicSearchTerm);
    }
  }, [currentFlow, panelStep, clinicSearchTerm, debouncedSearch]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Fetch veterinarians when clinic is selected
  useEffect(() => {
    const fetchVeterinarians = async () => {
      if (selectedClinicId && panelStep === 2) {
        try {
          setLoading(true);
          const response = await clinicAPI.getVeterinarians({
            clinic_id: selectedClinicId,
          });
          setClinicMembers(response.data?.data || response.data || []);

          // Also fetch working hours for calendar
          const hoursResponse = await clinicAPI.getWorkingHours(selectedClinicId);
          const workingHoursData = hoursResponse.data?.data?.working_hours || hoursResponse.data?.working_hours || [];
          setWorkingHours(workingHoursData);
        } catch (err) {
          setError(t("appointmentRequest.errors.loadVeterinarians"));
        } finally {
          setLoading(false);
        }
      }
    };

    fetchVeterinarians();
  }, [selectedClinicId, panelStep, t]);

  // Keep selectedVetRef in sync with selectedVet state
  useEffect(() => {
    selectedVetRef.current = selectedVet;
  }, [selectedVet]);

  // Fetch available time slots from API
  const fetchAvailableTimeSlots = async (vetId, date) => {
    if (!vetId || !date) {
      return [];
    }

    try {
      setLoading(true);
      const response = await clinicAPI.getVetAvailability(vetId, date);

      // Extract available slots from the API response
      // The structure is: response.data.data.availability.available_slots
      let slots = [];

      if (response.data) {
        slots = response.data.data?.availability?.available_slots ||
          response.data.data?.available_slots ||
          response.data.data?.slots ||
          response.data.available_slots ||
          response.data.slots ||
          [];
      }

      // Only map if slots is an array and has items
      if (!Array.isArray(slots) || slots.length === 0) {
        return [];
      }

      // Transform the API response to match our slot format
      const transformedSlots = slots.map(slot => ({
        time: slot.time || slot.start_time || slot.time_slot,
        endTime: slot.end_time || slot.end_time_slot,
        duration: slot.duration || '30 min',
        remaining: slot.remaining || slot.available_slots || slot.count || 0,
        available: slot.is_available !== false && slot.available !== false && !slot.is_booked && slot.status !== 'booked'
      }));

      return transformedSlots;
    } catch (err) {
      console.error("Error fetching available time slots:", err);
      setError(t("appointmentRequest.errors.loadSlots"));
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Generate available time slots (fallback)
  const generateTimeSlots = (workingHours, selectedDate) => {
    if (!workingHours.length || !selectedDate) return [];

    // Use consistent day name mapping with timezone-safe parsing
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    // Parse date safely to avoid timezone issues
    // Assuming selectedDate is in format "YYYY-MM-DD"
    const [year, month, day] = selectedDate.split('-').map(Number);
    const dateObj = new Date(year, month - 1, day); // month is 0-indexed
    const selectedDay = dayNames[dateObj.getDay()];



    const daySchedule = workingHours.find(wh =>
      wh.day && wh.day.toLowerCase() === selectedDay
    );

    if (!daySchedule || daySchedule.is_closed) {
      return [];
    }

    const openTime = daySchedule.open_time || daySchedule.opening_time;
    const closeTime = daySchedule.close_time || daySchedule.closing_time;

    if (!openTime || !closeTime) {
      return [];
    }

    const startMinutes = timeToMinutes(openTime);
    const endMinutes = timeToMinutes(closeTime);
    const slots = [];

    for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
      const timeString = minutesToTime(minutes);
      const endTimeString = minutesToTime(minutes + 30);

      slots.push({
        time: timeString,
        endTime: endTimeString,
        duration: '30 min',
        remaining: 1, // Each slot is for one appointment
        available: true
      });
    }

    return slots;
  };

  // Handle pet selection
  const handlePetSelect = (petId) => {
    setSelectedPet(petId);
    setCurrentFlow('booking-panel');
    setPanelStep(1);
  };

  // Handle clinic selection
  const handleClinicSelect = (clinicId) => {
    setSelectedClinicId(clinicId);
    setPanelStep(2);
  };

  // Cleanup calendar function
  const cleanupCalendar = () => {
    if (datePickerRef.current) {
      datePickerRef.current.destroy();
      datePickerRef.current = null;
    }
  };

  // Handle vet selection
  const handleVetSelect = (vetId) => {
    // Reset date/time selections when selecting a new vet
    setSelectedDate(null);
    setSelectedTime(null);
    setAvailableTimeSlots([]);

    // Cleanup existing calendar
    cleanupCalendar();

    setSelectedVet(vetId);
    setPanelStep(3);

    // Initialize calendar
    setTimeout(() => {
      if (dateInputRef.current) {
        // Helper to get local date string YYYY-MM-DD
        // This ensures the "today" date is correctly calculated based on the user's timezone
        const getLocalDateString = () => {
          return new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0];
        };
        const localToday = getLocalDateString();

        // Helper function to check if a day has available slots
        const dayHasAvailableSlots = (dayOfWeek) => {
          // If working hours aren't loaded yet, allow all days
          if (!workingHours || workingHours.length === 0) {
            return true;
          }

          const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
          const dayName = dayNames[dayOfWeek];

          const daySchedule = workingHours.find(wh => {
            return wh && wh.day && wh.day.toLowerCase() === dayName;
          });

          if (!daySchedule || daySchedule.is_closed) {
            return false;
          }

          const openTime = daySchedule.open_time || daySchedule.opening_time;
          const closeTime = daySchedule.close_time || daySchedule.closing_time;

          if (!openTime || !closeTime) {
            return false;
          }

          // Generate time slots for this day to check if any are available
          const startMinutes = timeToMinutes(openTime);
          const endMinutes = timeToMinutes(closeTime);

          // Check if there's at least one 30-minute slot available
          return (endMinutes - startMinutes) >= 30;
        };

        datePickerRef.current = flatpickr(dateInputRef.current, {
          inline: true,
          dateFormat: "Y-m-d",
          minDate: localToday,
          showMonths: 1,
          disableMobile: true,
          // Don't disable any days initially - let them all be selectable
          disable: workingHours && workingHours.length > 0 ? [
            function (date) {
              return !dayHasAvailableSlots(date.getDay());
            }
          ] : [],
          onReady: function (selectedDates, dateStr, instance) {
            // Add custom classes for mobile-first styling
            const calendar = instance.calendarContainer;
            calendar.classList.add('sulhafa-calendar-guardian');

            // Add touch-friendly styling
            const days = calendar.querySelectorAll('.flatpickr-day');
            days.forEach(day => {
              day.classList.add('touch-friendly');
            });
          },
          onChange: async function (selectedDates) {
            if (selectedDates.length > 0) {
              // Use local date conversion to avoid timezone shifts
              // flatpickr returns Date object in browser local time
              const selectedDateObj = selectedDates[0];
              const year = selectedDateObj.getFullYear();
              const month = String(selectedDateObj.getMonth() + 1).padStart(2, '0');
              const day = String(selectedDateObj.getDate()).padStart(2, '0');
              const date = `${year}-${month}-${day}`;

              setSelectedDate(date);
              // Fetch available time slots from API using the ref to get latest value
              const slots = await fetchAvailableTimeSlots(selectedVetRef.current, date);
              setAvailableTimeSlots(slots);
            }
          }
        });
      }
    }, 100);
  };

  // Handle time selection
  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  // Handle booking confirmation
  const handleConfirmBooking = async () => {
    try {
      setLoading(true);
      const petObj = pets.find(p => p.id === selectedPet);
      const vetObj = clinicMembers.find(v => v.user_id === selectedVet);

      // Try multiple methods to get user ID
      let currentUserId = user?.id || userId;

      // If still no user ID, try authUtils
      if (!currentUserId) {
        const authUser = authUtils.getCurrentUser();
        currentUserId = authUser?.id || authUser?.user_id;
      }

      // If still no user ID, try direct localStorage access
      if (!currentUserId) {
        const token = localStorage.getItem("access_token");
        const userDataString = localStorage.getItem("user_data");

        if (userDataString && userDataString !== 'undefined' && userDataString !== 'null') {
          try {
            const userData = JSON.parse(userDataString);
            currentUserId = userData?.id || userData?.user_id;
          } catch (e) {
            console.error("Error parsing user data from localStorage:", e);
          }
        }
      }

      if (!currentUserId) {
        setError(t("appointmentRequest.errors.userMissing"));
        setLoading(false);
        return;
      }

      const payload = {
        pet_id: selectedPet,
        clinic_id: selectedClinicId,
        vet_id: selectedVet,
        appointment_type_id: 1,
        pet_owner_id: currentUserId,
        appointment_at: selectedDate && selectedTime ? `${selectedDate} ${selectedTime}` : "",
        reason: reasonForVisit.trim() || "General checkup", // Use user-provided reason or default
      };

      await createClinicAppointment(payload);
      setSuccess(t("appointmentRequest.success.message"));
      setCurrentFlow('confirmation');
    } catch (err) {
      setError(err?.response?.data?.message || t("appointmentRequest.errors.createAppointment"));
    } finally {
      setLoading(false);
    }
  };

  // Handle going back to clinic selection
  const handleBackToClinic = () => {
    setSelectedVet(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setAvailableTimeSlots([]);
    cleanupCalendar();
    setPanelStep(1);
  };

  // Handle going back to vet selection
  const handleBackToVet = () => {
    setSelectedDate(null);
    setSelectedTime(null);
    setAvailableTimeSlots([]);
    cleanupCalendar();
    setPanelStep(2);
  };

  // Handle edit appointment from confirmation
  const handleEditAppointment = () => {
    setCurrentFlow('booking-panel');
    setPanelStep(3);

    // Cleanup existing calendar
    cleanupCalendar();

    // Reinitialize calendar with current selections
    setTimeout(() => {
      if (dateInputRef.current) {
        // Helper to get local date string YYYY-MM-DD
        // This ensures the "today" date is correctly calculated based on the user's timezone
        const getLocalDateString = () => {
          return new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0];
        };
        const localToday = getLocalDateString();

        // Helper function to check if a day has available slots
        const dayHasAvailableSlots = (dayOfWeek) => {
          // If working hours aren't loaded yet, allow all days
          if (!workingHours || workingHours.length === 0) {
            return true;
          }

          const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
          const dayName = dayNames[dayOfWeek];

          const daySchedule = workingHours.find(wh => {
            return wh && wh.day && wh.day.toLowerCase() === dayName;
          });

          if (!daySchedule || daySchedule.is_closed) {
            return false;
          }

          const openTime = daySchedule.open_time || daySchedule.opening_time;
          const closeTime = daySchedule.close_time || daySchedule.closing_time;

          if (!openTime || !closeTime) {
            return false;
          }

          // Generate time slots for this day to check if any are available
          const startMinutes = timeToMinutes(openTime);
          const endMinutes = timeToMinutes(closeTime);

          // Check if there's at least one 30-minute slot available
          return (endMinutes - startMinutes) >= 30;
        };

        datePickerRef.current = flatpickr(dateInputRef.current, {
          inline: true,
          dateFormat: "Y-m-d",
          minDate: localToday,
          defaultDate: selectedDate || localToday, // Pre-select the current date
          disableMobile: true,
          // Don't disable any days initially - let them all be selectable
          disable: workingHours && workingHours.length > 0 ? [
            function (date) {
              return !dayHasAvailableSlots(date.getDay());
            }
          ] : [],
          onChange: async function (selectedDates) {
            if (selectedDates.length > 0) {
              // Use local date conversion to avoid timezone shifts
              // flatpickr returns Date object in browser local time
              const selectedDateObj = selectedDates[0];
              const year = selectedDateObj.getFullYear();
              const month = String(selectedDateObj.getMonth() + 1).padStart(2, '0');
              const day = String(selectedDateObj.getDate()).padStart(2, '0');
              const date = `${year}-${month}-${day}`;

              setSelectedDate(date);

              // Get the current vet ID from the ref to get latest value
              const currentVetId = selectedVetRef.current;
              if (!currentVetId) {
                setAvailableTimeSlots([]);
                return;
              }

              // Fetch available time slots from API
              const slots = await fetchAvailableTimeSlots(currentVetId, date);
              setAvailableTimeSlots(slots);
            }
          }
        });

        // If we have a selected date, fetch available time slots from API
        if (selectedDate && selectedVetRef.current) {
          fetchAvailableTimeSlots(selectedVetRef.current, selectedDate).then(slots => {
            setAvailableTimeSlots(slots);
          });
        }
      }
    }, 100);
  };

  // Reset flow
  const resetFlow = () => {
    cleanupCalendar();
    setCurrentFlow('pet-selection');
    setPanelStep(1);
    setSelectedPet(null);
    setSelectedClinicId(null);
    setSelectedVet(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setAvailableTimeSlots([]);
    setError("");
    setSuccess("");
  };

  // Get selected pet object
  const selectedPetObj = Array.isArray(pets) ? pets.find(pet => pet.id === selectedPet) : null;
  const selectedClinicObj = Array.isArray(clinics) ? clinics.find(clinic => clinic.id === selectedClinicId) : null;
  const selectedVetObj = Array.isArray(clinicMembers) ? clinicMembers.find(vet => vet.user_id === selectedVet) : null;

  // Use clinics directly from API search results
  const filteredClinics = Array.isArray(clinics) ? clinics : [];

  const getSpeciesLabel = (pet) => {
    if (!pet) return t("appointmentRequest.petSelection.unknownSpecies");
    const species =
      typeof pet.species === "object"
        ? pet.species?.name || pet.species?.id
        : pet.species;
    return species || t("appointmentRequest.petSelection.unknownSpecies");
  };

  const getBreedLabel = (pet) => {
    if (!pet) return t("appointmentRequest.petSelection.unknownBreed");
    const breed =
      typeof pet.breed === "object"
        ? pet.breed?.name || pet.breed?.id
        : pet.breed;
    return breed || t("appointmentRequest.petSelection.unknownBreed");
  };

  const getAgeLabel = (age) => {
    if (!age && age !== 0) {
      return t("appointmentRequest.petSelection.unknownAge");
    }
    return t("appointmentRequest.petSelection.age", { age });
  };

  return (
    <div
      className={`appointment-new-root ${isRTL ? "rtl" : ""}`}
      dir={i18n.dir()}
      lang={i18n.language}
    >
      {/* Pet Selection Screen */}

      {/* Pet Selection Screen */}
      {currentFlow === "pet-selection" && (
        <div className="pet-selection-screen">
          <button
            className="pet-selection-back-btn"
            onClick={() => navigate(getDashboardUrl() + '?tab=appointments')}
          >
            <ArrowLeft size={20} />
            <span>{t("common.back", "Back")}</span>
          </button>
          <div className="pet-selection-container">
            <h1 className="pet-selection-title">
              {t("appointmentRequest.petSelection.title")}
            </h1>
            <p className="pet-selection-subtitle">
              {t("appointmentRequest.petSelection.subtitle")}
            </p>

            {loading ? (
              <div className="loading-spinner">
                {t("appointmentRequest.loading.pets")}
              </div>
            ) : (
              <div className="pet-selection-grid">
                {Array.isArray(pets) && pets.length > 0 ? pets.map((pet) => (
                  <div
                    key={pet.id}
                    className="pet-selection-card"
                    onClick={() => handlePetSelect(pet.id)}
                  >
                    <div className="pet-avatar">
                      {(() => {
                        const petImageUrl = resolveImageUrl(
                          pet.primaryImageUrl,
                          pet.primary_image_url,
                          pet.profile_image_path,
                          pet.profile_image_url,
                          pet.profileImageUrl,
                          pet.image,
                          pet.avatar,
                          pet.photo,
                          pet.pictures?.[0]?.url,
                          pet.media?.[0]?.url
                        );
                        const hasImage = petImageUrl && !petImageUrl.includes('placeholder') && !petImageUrl.includes('no-image');

                        return hasImage ? (
                          <img
                            src={petImageUrl}
                            alt={pet.name}
                            className="pet-selection-image"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null;
                      })()}
                      <div className="pet-avatar-fallback" style={{
                        display: (() => {
                          const petImageUrl = resolveImageUrl(
                            pet.primaryImageUrl,
                            pet.primary_image_url,
                            pet.profile_image_path,
                            pet.profile_image_url,
                            pet.profileImageUrl,
                            pet.image,
                            pet.avatar,
                            pet.photo,
                            pet.pictures?.[0]?.url,
                            pet.media?.[0]?.url
                          );
                          const hasImage = petImageUrl && !petImageUrl.includes('placeholder') && !petImageUrl.includes('no-image');
                          return hasImage ? 'none' : 'flex';
                        })()
                      }}>
                        {(typeof pet.species === "object" ? pet.species?.name : pet.species) === "Dog"
                          ? "🐕"
                          : (typeof pet.species === "object" ? pet.species?.name : pet.species) === "Cat"
                            ? "🐱"
                            : "🐾"}
                      </div>
                    </div>
                    <div className="pet-selection-info">
                      <h3 className="pet-selection-name">{pet.name}</h3>
                      <p className="pet-selection-details">
                        {getSpeciesLabel(pet)} • {getBreedLabel(pet)}
                      </p>
                      <p className="pet-selection-age">
                        {getAgeLabel(pet.age)}
                      </p>
                    </div>
                  </div>
                )) : (
                  <div className="no-clinics-message">
                    <p>{t("appointmentRequest.petSelection.empty")}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Booking Panel */}
      {currentFlow === 'booking-panel' && (
        <div className="booking-panel-overlay">
          {/* Selected Pet Display */}
          <div className="selected-pet-display">
            {selectedPetObj && (
              <div className="selected-pet-card">
                <div className="pet-avatar">
                  {(() => {
                    const petImageUrl = resolveImageUrl(
                      selectedPetObj.primaryImageUrl,
                      selectedPetObj.primary_image_url,
                      selectedPetObj.profile_image_path,
                      selectedPetObj.profile_image_url,
                      selectedPetObj.profileImageUrl,
                      selectedPetObj.image,
                      selectedPetObj.avatar,
                      selectedPetObj.photo,
                      selectedPetObj.pictures?.[0]?.url,
                      selectedPetObj.media?.[0]?.url
                    );
                    const hasImage = petImageUrl && !petImageUrl.includes('placeholder') && !petImageUrl.includes('no-image');

                    return hasImage ? (
                      <img
                        src={petImageUrl}
                        alt={selectedPetObj.name}
                        className="selected-pet-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null;
                  })()}
                  <div className="pet-avatar-fallback" style={{
                    display: (() => {
                      const petImageUrl = resolveImageUrl(
                        selectedPetObj.primaryImageUrl,
                        selectedPetObj.primary_image_url,
                        selectedPetObj.profile_image_path,
                        selectedPetObj.profile_image_url,
                        selectedPetObj.profileImageUrl,
                        selectedPetObj.image,
                        selectedPetObj.avatar,
                        selectedPetObj.photo,
                        selectedPetObj.pictures?.[0]?.url,
                        selectedPetObj.media?.[0]?.url
                      );
                      const hasImage = petImageUrl && !petImageUrl.includes('placeholder') && !petImageUrl.includes('no-image');
                      return hasImage ? 'none' : 'flex';
                    })()
                  }}>
                    {(typeof selectedPetObj.species === 'object' ? selectedPetObj.species?.name : selectedPetObj.species) === 'Dog' ? '🐕' : (typeof selectedPetObj.species === 'object' ? selectedPetObj.species?.name : selectedPetObj.species) === 'Cat' ? '🐱' : '🐾'}
                  </div>
                </div>
                <div className="selected-pet-info">
                  <h4>{selectedPetObj.name}</h4>
                  <p>
                    {getSpeciesLabel(selectedPetObj)} • {getBreedLabel(selectedPetObj)}
                  </p>
                </div>
                <button className="change-pet-btn" onClick={resetFlow}>
                  {t("appointmentRequest.booking.changePet")}
                </button>
              </div>
            )}
          </div>

          {/* Main Booking Panel */}
          <div className="booking-panel">
            <div className="booking-panel-header">
              <h2>{t("appointmentRequest.booking.title")}</h2>
              <div className="booking-panel-header-right">
                <div className="booking-steps">
                  <div
                    className={`booking-step ${panelStep === 1 ? "active" : panelStep > 1 ? "completed" : ""
                      }`}
                  >
                    <span className="step-index">1</span>
                    <span className="step-label">
                      {t("appointmentRequest.steps.clinic")}
                    </span>
                  </div>
                  <div
                    className={`booking-step ${panelStep === 2 ? "active" : panelStep > 2 ? "completed" : ""
                      }`}
                  >
                    <span className="step-index">2</span>
                    <span className="step-label">
                      {t("appointmentRequest.steps.vet")}
                    </span>
                  </div>
                  <div
                    className={`booking-step ${panelStep === 3 ? "active" : ""}`}
                  >
                    <span className="step-index">3</span>
                    <span className="step-label">
                      {t("appointmentRequest.steps.datetime")}
                    </span>
                  </div>
                </div>
                <button
                  className="close-panel-btn"
                  onClick={resetFlow}
                  aria-label={t("appointmentRequest.booking.close")}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="booking-panel-content">
              {/* Step 1: Clinic Selection */}
              {panelStep === 1 && (
                <div className="panel-step clinic-selection-step">
                  <h3>{t("appointmentRequest.booking.clinic.title")}</h3>
                  <div className="clinic-search-container">
                    <input
                      type="text"
                      placeholder={t("appointmentRequest.booking.clinic.searchPlaceholder")}
                      value={clinicSearchTerm}
                      onChange={(e) => setClinicSearchTerm(e.target.value)}
                      className="clinic-search-input"
                    />
                  </div>
                  {loading ? (
                    <div className="loading-spinner">
                      {t("appointmentRequest.loading.clinics")}
                    </div>
                  ) : (
                    <div className="clinic-grid-new">
                      {Array.isArray(filteredClinics) && filteredClinics.length > 0 ? filteredClinics.map((clinic) => (
                        <div
                          key={clinic.id}
                          className="clinic-card-new"
                          onClick={() => handleClinicSelect(clinic.id)}
                        >
                          <div className="clinic-avatar">
                            {clinic.logo?.url || clinic.logo ? (
                              <img
                                src={clinic.logo?.url || clinic.logo}
                                alt={clinic.name_en || clinic.name_ar}
                                className="clinic-card-image"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div className="clinic-avatar-fallback" style={{
                              display: (clinic.logo?.url || clinic.logo) ? 'none' : 'flex'
                            }}>
                              🏥
                            </div>
                          </div>
                          <div className="clinic-card-info">
                            <h4>{clinic.name_en || clinic.name_ar}</h4>
                            <p>{clinic.address}</p>
                            <p>{clinic.mobile || clinic.telephone}</p>
                            {clinic.average_rating !== null &&
                              clinic.average_rating !== undefined &&
                              typeof clinic.ratings_count === "number" &&
                              clinic.ratings_count > 0 && (
                                <div className="clinic-card-rating">
                                  <UserAverageRating
                                    averageRating={clinic.average_rating}
                                    ratingsCount={clinic.ratings_count}
                                    ratingLabel={null}
                                    size="small"
                                  />
                                  <span className="clinic-card-rating-count">
                                    ({clinic.ratings_count})
                                  </span>
                                </div>
                              )}
                          </div>
                        </div>
                      )) : (
                        <div className="no-clinics-message">
                          <p>
                            {clinicSearchTerm.length > 0 && clinicSearchTerm.length < 2
                              ? t("appointmentRequest.booking.clinic.searchHint")
                              : clinicSearchTerm
                                ? t("appointmentRequest.booking.clinic.noResults", { term: clinicSearchTerm })
                                : t("appointmentRequest.booking.clinic.empty")}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Vet Selection */}
              {panelStep === 2 && (
                <div className="panel-step vet-selection-step">
                  <div className="step-header">
                    <button className="back-btn" onClick={handleBackToClinic}>
                      ← {t("appointmentRequest.common.back")}
                    </button>
                    <h3>{t("appointmentRequest.booking.vet.title")}</h3>
                  </div>
                  {selectedClinicObj && (
                    <div className="selected-clinic-info">
                      <strong>{selectedClinicObj.name_en || selectedClinicObj.name_ar}</strong>
                    </div>
                  )}
                  {loading ? (
                    <div className="loading-spinner">
                      {t("appointmentRequest.loading.vets")}
                    </div>
                  ) : (
                    <div className="vet-list">
                      {clinicMembers.map((vet) => (
                        <div
                          key={vet.user_id}
                          className={`vet-card ${selectedVet === vet.user_id ? 'selected' : ''}`}
                          onClick={() => handleVetSelect(vet.user_id)}
                        >
                          <img
                            src={vet.profile_image || '/images/default.jpg'}
                            alt={vet.full_name}
                            className="vet-image"
                          />
                          <div className="vet-info">
                            <h4>{vet.full_name}</h4>
                            <p>{vet.specialization || t("appointmentRequest.booking.vet.generalSpecialist")}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Date & Time Selection */}
              {panelStep === 3 && (
                <div className="panel-step datetime-selection-step">
                  <div className="step-header">
                    <button className="back-btn" onClick={handleBackToVet}>
                      ← {t("appointmentRequest.common.back")}
                    </button>
                    <h3>{t("appointmentRequest.datetime.title")}</h3>
                  </div>
                  {selectedVetObj && (
                    <div className="selected-vet-info">
                      <strong>{t("appointmentRequest.booking.vet.selected", { name: selectedVetObj.full_name })}</strong>
                    </div>
                  )}

                  <div className="datetime-container">
                    <div className="calendar-container">
                      <input
                        ref={dateInputRef}
                        type="text"
                        style={{ display: 'none' }}
                        readOnly
                      />
                    </div>

                    <div className="time-slots-container">
                      <h4>{t("appointmentRequest.datetime.availableTimes")}</h4>
                      {selectedDate ? (
                        (() => {
                          const visibleSlots = availableTimeSlots.filter(slot =>
                            typeof slot === 'object' ? slot.available !== false : true
                          );

                          return visibleSlots.length > 0 ? (
                            <div className="time-slots-grid">
                              {visibleSlots.map((slot, index) => {
                                // Handle both object and string formats
                                const slotTime = typeof slot === 'object' ? slot.time : slot;
                                const slotKey = typeof slot === 'object' ? slot.time : `${slot}-${index}`;

                                return (
                                  <button
                                    key={slotKey}
                                    className={`time-slot ${selectedTime === slotTime ? 'selected' : ''}`}
                                    onClick={() => handleTimeSelect(slotTime)}
                                  >
                                    <div className="time-main">{slotTime}</div>
                                    {typeof slot === 'object' && (
                                      <div className="time-details">
                                        <span className="duration">{slot.duration}</span>
                                        <span className="remaining">
                                          {t("appointmentRequest.datetime.availableStatus")}
                                        </span>
                                      </div>
                                    )}
                                    {typeof slot === 'string' && (
                                      <div className="time-details">
                                        <span className="duration">
                                          {t("appointmentRequest.datetime.defaultDuration")}
                                        </span>
                                        <span className="remaining">
                                          {t("appointmentRequest.datetime.availableStatus")}
                                        </span>
                                      </div>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="no-slots-message">
                              <p>{t("appointmentRequest.datetime.noSlots")}</p>
                              <div className="loading-skeleton">
                                <div className="skeleton-category"></div>
                                <div className="skeleton-slots">
                                  {[...Array(6)].map((_, i) => (
                                    <div key={i} className="skeleton-slot"></div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                        })()
                      ) : (
                        <p className="select-date-prompt">
                          {t("appointmentRequest.datetime.selectDatePrompt")}
                        </p>
                      )}
                    </div>
                  </div>

                  {selectedDate && selectedTime && (
                    <button
                      className="confirm-selection-btn"
                      onClick={() => setCurrentFlow('confirmation')}
                    >
                      {t("appointmentRequest.datetime.reviewButton")}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Screen */}
      {currentFlow === 'confirmation' && (
        <div className="confirmation-screen">
          <div className="confirmation-container">
            <h2>{t("appointmentRequest.summary.title")}</h2>

            <div className="appointment-summary">
              <div className="summary-section">
                <h4>{t("appointmentRequest.summary.pet")}</h4>
                <p>
                  {selectedPetObj?.name} ({getSpeciesLabel(selectedPetObj)})
                </p>
              </div>

              <div className="summary-section">
                <h4>{t("appointmentRequest.summary.clinic")}</h4>
                <p>{selectedClinicObj?.name_en || selectedClinicObj?.name_ar}</p>
              </div>

              <div className="summary-section">
                <h4>{t("appointmentRequest.summary.vet")}</h4>
                <p>{t("appointmentRequest.summary.vetName", { name: selectedVetObj?.full_name })}</p>
              </div>

              <div className="summary-section">
                <h4>{t("appointmentRequest.summary.dateTimeLabel")}</h4>
                <p>{t("appointmentRequest.summary.dateTime", { date: selectedDate, time: selectedTime })}</p>
              </div>
            </div>

            <div className="reason-for-visit-section">
              <label htmlFor="reason-for-visit" className="reason-for-visit-label">
                {t("appointmentRequest.summary.reasonForVisit.label")} <span className="optional-text">({t("appointmentRequest.summary.reasonForVisit.optional")})</span>
              </label>
              <textarea
                id="reason-for-visit"
                className="reason-for-visit-input"
                placeholder={t("appointmentRequest.summary.reasonForVisit.placeholder")}
                value={reasonForVisit}
                onChange={(e) => setReasonForVisit(e.target.value)}
                rows={3}
                maxLength={500}
              />
              {reasonForVisit.length > 0 && (
                <div className="character-count">
                  {reasonForVisit.length}/500
                </div>
              )}
            </div>

            <div className="confirmation-actions">
              <button
                className="back-to-panel-btn"
                onClick={handleEditAppointment}
              >
                {t("appointmentRequest.summary.edit")}
              </button>
              <button
                className="confirm-booking-btn"
                onClick={handleConfirmBooking}
                disabled={loading}
              >
                {loading
                  ? t("appointmentRequest.summary.booking")
                  : t("appointmentRequest.summary.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Modals */}
      {success && (
        <div className="modal-overlay">
          <div className="modal-content success-modal">
            <h3>{t("appointmentRequest.success.title")}</h3>
            <p>{success}</p>
            <button onClick={() => navigate(getDashboardUrl())}>
              {t("appointmentRequest.success.button")}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="modal-overlay">
          <div className="modal-content error-modal">
            <h3>{t("appointmentRequest.errorModal.title")}</h3>
            <p>{error}</p>
            <button onClick={() => setError('')}>
              {t("appointmentRequest.errorModal.close")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentRequest;
